import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import MasterLayout from '../../components/layouts/masterlayout';
import ErrorMessage from '../../components/common/errormessage';
import SuccessMessage from '../../components/common/successmessage';
import PropTypes from 'prop-types';
import GeneralTextBox from '../common/textboxes/textbox_general';
import GeneralButton from '../common/button_general';
import Spinner from '../common/spinner';
import { getHeaders, handleErrorResponse, handleResponse } from '../../lib/requests';
import ClientModal from '../common/modals/client_modal';
import RouteClientsModal from './route_client_modal';

function RouteForm({ route = {} }) {
  const { routeId } = useParams();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [showError, setShowError] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const [formData, setFormData] = useState({
    route_id: routeId || '',
    name: route.name || '',
    reference_id: route.reference_id || '',
    create_date: route.create_date || '',
    modified_date: route.modified_date || ''
  });

  const [isClientModalOpen, setIsClientModalOpen] = useState(false);
  const [isRouteClientsModalOpen, setIsRouteClientsModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState({ client_id: null, name: '' });

  const handleClientSelect = (client) => {
    setSelectedClient(client);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  useEffect(() => {
    const fetchRouteData = async () => {
      if (!routeId) return;
      try {
        const apiToken = localStorage.getItem('accessToken');
        const apiUrl = process.env.REACT_APP_API_URL;
        setIsLoading(true);
        const response = await axios.get(`${apiUrl}/routes/${routeId}`, { headers: getHeaders(apiToken) });
        const routeData = response.data.data;
        setFormData({ ...routeData });
        setShowError(false);
      } catch (error) {
        handleErrorResponse(error, navigate, setShowError, setErrorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRouteData();
  }, [routeId]);

  const resetForm = () => {
    setSuccessMessage('');
    setErrorMessage('');
    setShowError(false);
    setShowSuccess(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const apiToken = localStorage.getItem('accessToken') || '';
    const apiUrl = process.env.REACT_APP_API_URL || '';
    resetForm();

    if (!apiToken || !apiUrl) {
      console.error('API token or URL is missing');
      setShowError(true);
      setErrorMessage('Configuration error');
      return;
    }

    const headers = getHeaders(apiToken);
    const url = routeId ? `${apiUrl}/routes/${routeId}` : `${apiUrl}/routes`;
    const method = routeId ? 'put' : 'post';

    try {
      const response = await axios({
        method,
        url,
        data: JSON.stringify(formData),
        headers,
      });

      handleResponse(response,
        "Route",
        setShowSuccess,
        setSuccessMessage,
        setShowError,
        setErrorMessage,
        navigate,
        '/routes/:id')
    } catch (error) {
      handleErrorResponse(error, navigate, setShowError, setErrorMessage);
    }
  };

  const handleAddClientToRoute = async () => {
    const apiToken = localStorage.getItem('accessToken') || '';
    const apiUrl = process.env.REACT_APP_API_URL || '';
    resetForm();

    if (!apiToken || !apiUrl || !selectedClient.client_id || !routeId) {
      console.error('Required data is missing');
      setShowError(true);
      setErrorMessage('Configuration error');
      return;
    }

    const headers = getHeaders(apiToken);
    const url = `${apiUrl}/routes/add-client`;

    try {
      const response = await axios.post(url, {
        client_id: selectedClient.client_id,
        route_id: routeId
      }, { headers });

      handleResponse(response,
        "Client",
        setShowSuccess,
        setSuccessMessage,
        setShowError,
        setErrorMessage,
        navigate,
        `/routes/${routeId}`)
    } catch (error) {
      handleErrorResponse(error, navigate, setShowError, setErrorMessage);
    }
  };

  const handleBack = () => navigate('/routes');

  const pageTitle = routeId ? 'Edit ' + formData.name : 'Create Route';

  return (
    <MasterLayout pageTitle={pageTitle}>
      {showError && <ErrorMessage message={errorMessage} />}
      {showSuccess && <SuccessMessage message={successMessage} setShowSuccess={setShowSuccess} />}
      {isLoading ? <Spinner /> : (
        <form onSubmit={handleSubmit} className="p-4">
          <GeneralTextBox
            label="Route Name" id="name" name="name" type="text"
            value={formData.name} onChange={handleChange}
            placeholder="Route Name" required={true}
          />
          {/* Removed Delivery Day of the Week dropdown */}
          <GeneralTextBox
            label="Reference ID" id="reference_id" name="reference_id" type="text"
            value={formData.reference_id} onChange={handleChange}
            placeholder="Reference ID"
          />
          <GeneralTextBox
            label="External Reference ID" id="external_reference_id" name="external_reference_id" type="text"
            value={formData.external_reference_id} onChange={handleChange}
            placeholder="External Reference ID"
          />
          <div className="flex justify-center mt-6">
            <GeneralButton type="submit" buttonType="primary">
              {routeId ? 'Update Route' : 'Create Route'}
            </GeneralButton>
            <GeneralButton type="button" buttonType="secondary" onClick={handleBack}>
              Back to Route List
            </GeneralButton>
          </div>
          <div className="flex justify-center mt-6">
            <RouteClientsModal
              isOpen={isRouteClientsModalOpen}
              setIsOpen={setIsRouteClientsModalOpen}
              routeId={routeId}
            />
          </div>
        </form>
      )}

      <div className="flex justify-center mt-6">
        <ClientModal
          isOpen={isClientModalOpen}
          setIsOpen={setIsClientModalOpen}
          selectClient={handleClientSelect}
          value={selectedClient}
        />
      </div>

      {selectedClient.client_id && (
        <div className="flex justify-center mt-6">
          <GeneralButton type="button" buttonType="primary" onClick={handleAddClientToRoute}>
            Add Client to Route
          </GeneralButton>
        </div>
      )}
    </MasterLayout>
  );
}

RouteForm.propTypes = {
  route: PropTypes.shape({
    name: PropTypes.string,
    reference_id: PropTypes.string,
    create_date: PropTypes.string,
    modified_date: PropTypes.string,
  }),
};

export default RouteForm;
